import type {ReactElement} from 'react';
import {useState} from 'react';
import {getAvatarColorClassName} from '@Libraries/user-avatar.library';
import type {UserAvatarProps} from './user-avatar.types';
import {AVATAR_INITIAL_FONT_SIZE, AVATAR_VIEWBOX_SIZE} from './user-avatar.types';
import styles from './user-avatar.module.scss';

/**
 * A person anywhere in the product: their own picture when they have one, otherwise their initial
 * on the colour their hashed id gives them, so the same person looks the same on every surface.
 * The initial is an SVG whose letter is half the circle, so it scales with whatever size it is given.
 * Announced by name unless the name is already text beside it.
 */
export function UserAvatar({avatar, name, size, hasNameBeside = false, className = ''}: UserAvatarProps): ReactElement {
  const [failedPictureUrl, setFailedPictureUrl] = useState<string | null>(null);
  const sizeClassName = styles[size];
  const circleCenter = AVATAR_VIEWBOX_SIZE / 2;

  if (avatar.pictureUrl !== null && avatar.pictureUrl !== failedPictureUrl) {
    return (
      <img
        src={avatar.pictureUrl}
        alt={hasNameBeside ? '' : name}
        title={name}
        className={`${styles.avatar} ${styles.picture} ${sizeClassName} ${className}`}
        onError={(): void => {
          setFailedPictureUrl(avatar.pictureUrl);
        }}
      />
    );
  }

  return (
    <svg
      viewBox={`0 0 ${AVATAR_VIEWBOX_SIZE} ${AVATAR_VIEWBOX_SIZE}`}
      role={hasNameBeside ? undefined : 'img'}
      aria-label={hasNameBeside ? undefined : name}
      aria-hidden={hasNameBeside}
      className={`${styles.avatar} ${styles[getAvatarColorClassName(avatar.colorIndex)]} ${sizeClassName} ${className}`}
    >
      <circle cx={circleCenter} cy={circleCenter} r={circleCenter} />
      <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize={AVATAR_INITIAL_FONT_SIZE}>
        {avatar.initial}
      </text>
    </svg>
  );
}
